export enum UserRank {
  INTERNAL = 'INTERNAL',
  INSTITUTION_ADMIN = 'INSTITUTION_ADMIN',
  INSTITUTION_USER = 'INSTITUTION_USER'
}

export enum DsrStatus {
  PENDING = 'PENDING',
  FORWARDED = 'FORWARDED',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  ON_HOLD = 'ON_HOLD',
  RETURNED = 'RETURNED',
  TESTING = 'TESTING',
  LIVE = 'LIVE'
}


export enum DsrEnvironment {
  TESTING = 'TESTING',
  PRODUCTION = 'PRODUCTION',
  SANDBOX = 'TESTING',
  TEST = 'TESTING'
}

export enum OnboardingStatus {
  PENDING = 'PENDING',
  DRAFT = 'DRAFT',
  APPROVED = 'APPROVED',
  ON_PROGRESS = 'ON_PROGRESS',
  REJECTED = 'REJECTED',
  CORRECTION = 'CORRECTION'
}

export interface ApiResponse<T> {
  success: boolean;
  code: string;
  message: string;
  data: T;
  timestamp?: string;
}

export interface PageResponse<T> {
  content: T[];
  pageNumber: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  isFirst: boolean;
  isLast: boolean;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface JwtAuthResponse {
  token: string;
  refreshToken?: string;
  tokenType: string;
  mfaRequired?: boolean;
  preAuthToken?: string;
  maskedEmail?: string;
  userUid: string;
  email: string;
  fullName: string;
  roles: string[];
  permissions?: string[];
  rank?: UserRank;
  institutionUid?: string;
  institutionName?: string;
  slaAccepted?: boolean;
  slaAcceptedAt?: string;
  slaAcceptedBy?: string;
}

export interface MfaVerifyRequest {
  preAuthToken: string;
  otp: string;
}

export interface ResendMfaRequest {
  preAuthToken: string;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface UserProfile {
  uid: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  phone: string;
  rank: UserRank;
  status: string;
  institutionUid?: string;
  institutionName?: string;
  roles: string[];
  lastLogin?: string;
  slaAccepted?: boolean;
  slaAcceptedAt?: string;
  slaAcceptedBy?: string;
  mfaEnabled?: boolean;
}

export interface SlaAgreementResponse {
  institutionUid: string;
  institutionName: string;
  slaAccepted: boolean;
  slaAcceptedAt?: string;
  slaAcceptedBy?: string;
  uptimeTarget: number;
  maxLatencyMs: number;
  incidentResponseMins: number;
  status: string;
  message?: string;
}

export type UserProfileResponse = UserProfile;

export interface DashboardMetricResponse {
  totalInstitutions: number;
  activeInstitutions: number;
  totalServices: number;
  activeServices: number;
  pendingDsrs: number;
  approvedDsrs: number;
  totalDsrs: number;
}

export interface DataSharingRequestProgressResponse {
  uid?: string;
  stageName?: string;
  stageOrder?: number;
  action?: string;
  status?: DsrStatus | string;
  comment?: string;
  createdByName?: string;
  performedBy?: string;
  createdAt?: string;
  timestamp?: string;
}

export interface AttachmentTypeDto {
  id: string;
  code: string;
  name: string;
  description?: string;
  isRequired: boolean;
  allowedExtensions?: string;
  maxSizeMb?: number;
  institutionUid?: string;
  institutionName?: string;
}

export interface CreateAttachmentTypeRequest {
  code?: string;
  name: string;
  description?: string;
  isRequired?: boolean;
  allowedExtensions?: string;
  maxSizeMb?: number;
}

export interface ServiceRequiredAttachmentDto {
  id?: string;
  name: string;
  code: string;
  description?: string;
  isRequired: boolean;
  allowedExtensions?: string;
  maxSizeMb?: number;
}

export interface ServiceDocumentationAttachmentDto {
  uid?: string;
  title: string;
  docType?: string;
  fileName: string;
  contentType?: string;
  fileSize?: number;
  fileData: string;
  uploadedAt?: string;
}

export interface CreateDsrAttachmentRequest {
  attachmentName: string;
  attachmentCode?: string;
  fileName: string;
  fileSize?: number;
  contentType?: string;
  fileData: string;
}

export interface DsrAttachmentResponse {
  uid: string;
  attachmentName: string;
  attachmentCode?: string;
  fileName: string;
  fileSize?: number;
  contentType?: string;
  fileData?: string;
  createdAt?: string;
}

export interface DataSharingRequestResponse {
  uid: string;
  fromInstitutionUid: string;
  fromInstitutionName: string;
  toInstitutionUid: string;
  toInstitutionName: string;
  serviceUid: string;
  serviceName: string;
  serviceCode: string;
  currentStageName: string;
  currentStageOrder: number;
  purpose: string;
  systemName: string;
  environment: DsrEnvironment;
  status: DsrStatus;
  approvedByName?: string;
  createdAt: string;
  updatedAt?: string;
  progressHistory?: DataSharingRequestProgressResponse[];
  attachments?: DsrAttachmentResponse[];
  serviceDocumentationAttachments?: ServiceDocumentationAttachmentDto[];
}

export interface ServiceCatalogResponse {
  uid: string;
  name: string;
  code: string;
  encodedId?: string;
  description: string;
  active: boolean;
  institutionUid: string;
  institutionName: string;
  approvedByName?: string;
  approvedOn?: string;
  requiredAttachments?: ServiceRequiredAttachmentDto[];
  documentationAttachments?: ServiceDocumentationAttachmentDto[];
  createdAt: string;
}

export interface OnboardingResponse {
  uid: string;
  institutionName: string;
  voteNumber: string;
  registrationNumber?: string;
  institutionEmail: string;
  institutionPhone: string;
  certificateNumber?: string;
  applicantName?: string;
  phone: string;
  token?: string;
  expirationDate?: string;
  status: OnboardingStatus;
  approvedOn?: string;
  memberClassCode?: string;
  memberClassName?: string;
  memberCode?: string;
  applicationLetter?: string;
  rejectionReason?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  applicantEmail?: string;
}

export interface OnboardingDecisionRequest {
  reason: string;
}

export interface WorkflowStageResponse {
  uid: string;
  name: string;
  stageOrder: number;
  institutionUid: string;
  institutionName?: string;
  assignedUsers: string[];
}

export interface WorkflowResponse {
  uid: string;
  name: string;
  code: string;
  stages: WorkflowStageResponse[];
}

export interface AuditLogResponse {
  id: number;
  userType: string;
  userId: number;
  event: string;
  auditableType: string;
  auditableId: number;
  oldValues?: string;
  newValues?: string;
  url?: string;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
}

export interface MemberClass {
  id?: number;
  code: string;
  name: string;
  description?: string;
}

export interface FileDto {
  content: string;
  fileType: string;
}

// Request Payload Interfaces
export interface LoginRequest {
  email: string;
  password: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ValidateOtpRequest {
  email: string;
  otp: string;
}

export interface ChangePasswordRequest {
  email: string;
  otp: string;
  newPassword: string;
}

export interface ResetPasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface CreateDsrRequest {
  toInstitutionUid: string;
  serviceUid: string;
  systemName: string;
  environment: DsrEnvironment;
  purpose: string;
  attachments?: CreateDsrAttachmentRequest[];
}

export interface ProcessDsrActionRequest {
  action: 'APPROVE' | 'REJECT' | 'FORWARD';
  comment?: string;
}

export interface RegisterServiceRequest {
  name: string;
  code: string;
  description: string;
  institutionUid: string;
}

export interface UpdateServiceRequest {
  name?: string;
  code?: string;
  encodedId?: string;
  description?: string;
  active?: boolean;
  requiredAttachments?: ServiceRequiredAttachmentDto[];
  documentationAttachments?: ServiceDocumentationAttachmentDto[];
}

export interface OnboardingRegisterRequest {
  institutionName: string;
  voteNumber?: string;
  registrationNumber?: string;
  certificateNumber?: string;
  memberClassCode?: string;
  institutionEmail: string;
  institutionPhone: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  applicantName?: string;
  phone: string;
  applicantEmail?: string;
  applicationLetter?: FileDto;
}

export interface OnboardingUserRequest {
  token: string;
  password: string;
  onboardingUid?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

export interface CreateUserRequest {
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  phone: string;
  rank: UserRank;
  institutionUid?: string;
  password?: string;
}

export interface UpdateUserRequest {
  firstName: string;
  middleName?: string;
  lastName: string;
  phone: string;
  status?: string;
  mfaEnabled?: boolean;
}

export interface UpdateMfaRequest {
  enabled: boolean;
}

export interface AssignRolesRequest {
  roles?: string[];
  roleNames?: string[];
}

export interface PermissionDto {
  id: number;
  name: string;
  description: string;
  category?: string;
}

export interface RoleDto {
  id: string;
  name: string;
  code: string;
  description: string;
  userCount: number;
  permissions: string[];
}

export interface CreateRoleRequest {
  name: string;
  code?: string;
  description?: string;
  permissions: string[];
}

export interface UpdateRolePermissionsRequest {
  permissions: string[];
}

export interface CreateWorkflowRequest {
  name: string;
  code: string;
}

export interface CreateWorkflowStageRequest {
  workflowUid: string;
  name: string;
  stageOrder: number;
  institutionUid: string;
}

export interface AssignStageUserRequest {
  workflowStageUid: string;
  userUid: string;
}

export interface InstitutionResponse {
  id?: number;
  uid: string;
  name: string;
  memberCode?: string;
  registrationNumber?: string;
  voteNumber?: string;
  email: string;
  phoneNumber: string;
  isActive: boolean;
  hasSs: boolean;
  ssApprovedByName?: string;
  ssApprovedOn?: string;
  approvalStatus: string;
  certificate?: string;
  applicationLetter?: string;
  memberClassCode?: string;
  memberClassName?: string;
  createdAt: string;
  updatedAt?: string;
  dpoName?: string;
  dpoEmail?: string;
  dpoPhone?: string;
  onboardingUid?: string;
  onboardingStatus?: string;
  slaAccepted?: boolean;
  slaAcceptedAt?: string;
  slaAcceptedBy?: string;
  rejectionReason?: string;
}

export interface UpdateInstitutionRequest {
  name: string;
  email: string;
  phoneNumber: string;
  registrationNumber?: string;
  voteNumber?: string;
  approvalStatus?: string;
  hasSs?: boolean;
  certificate?: string;
  applicationLetter?: string;
  dpoFirstName?: string;
  dpoLastName?: string;
  dpoEmail?: string;
  dpoPhone?: string;
}

export interface UpdateInstitutionStatusRequest {
  isActive: boolean;
}
